// src/components/pages/PromoPage/PromoPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, DevicesResponse } from '../../services/api_url';
import { useUserData } from '../../services/useUserData';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { BackIcon } from '../../assets/images/BackIcon';
import Loader from '../../widgets/Loader/Loader';
import {
    GiftIcon,
    CheckIcon,
    CloseIcon,
    CloseModalIcon,
    DeviceIcon as ReferralDeviceIcon
} from '../../assets/images/ReferralIcons';
import './PromoPage.scss';

interface PromoState {
    code: string;
    status: 'idle' | 'loading' | 'success' | 'error';
    message: string;
    discount?: number;
    promoType?: string;
}

// SVG Icons
const MoneyIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M12 6V8M12 16V18M8 12H6M18 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    </svg>
);

const LockIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="11" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
        <path d="M12 16V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const CalendarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="15" r="1" fill="currentColor"/>
        <circle cx="16" cy="15" r="1" fill="currentColor"/>
        <circle cx="8" cy="15" r="1" fill="currentColor"/>
    </svg>
);

const PercentIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 5L5 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <circle cx="17" cy="17" r="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    </svg>
);

const DeviceIcon = ({ type }: { type: string }) => {
    if (type === 'locked') {
        return (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="8" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M8 8V6C8 3.79 9.79 2 12 2C14.21 2 16 3.79 16 6V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 13V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="13" r="1" fill="currentColor"/>
            </svg>
        );
    }
    if (type === 'wireguard') {
        return (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
        );
    }
    if (type === 'rocket') {
        return (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M9 4L12 2L15 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 7L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M16 10L20 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M8 10L4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 22C14.5 22 16 19 16 17C16 14.5 14 12 12 12C10 12 8 14.5 8 17C8 19 9.5 22 12 22Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
            </svg>
        );
    }
    if (type === 'shield') {
        return (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L3 6V12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12V6L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        );
    }
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="6" width="16" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M12 6V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    );
};

const LoaderIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="spinner">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M12 2C6.477 2 2 6.477 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    </svg>
);

interface PromoPageProps {
    onModalStateChange?: (isOpen: boolean) => void;
}

function PromoPage({ onModalStateChange }: PromoPageProps) {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading } = useUserData();

    const [promoCode, setPromoCode] = useState<string>('');
    const [promoState, setPromoState] = useState<PromoState>({
        code: '',
        status: 'idle',
        message: ''
    });

    const [successDetails, setSuccessDetails] = useState<{
        deviceName: string;
        daysAdded: number;
        newExpiryDate: string;
        promoCode: string;
        isNewDevice?: boolean;
    } | null>(null);

    const [devices, setDevices] = useState<DevicesResponse | null>(null);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [applying, setApplying] = useState<boolean>(false);
    const [showDeviceSelection, setShowDeviceSelection] = useState<boolean>(false);
    const [appliedPromoData, setAppliedPromoData] = useState<any>(null);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    useEffect(() => {
        if (telegramId && showDeviceSelection) {
            loadDevices();
        }
    }, [telegramId, showDeviceSelection]);

    const loadDevices = async () => {
        if (!telegramId) return;

        try {
            const response = await apiService.getUserDevices(telegramId);
            setDevices(response);
        } catch (error) {
        }
    };

    const handleApplyPromo = async () => {
        if (!promoCode.trim() || !telegramId) return;

        setPromoState({
            code: promoCode,
            status: 'loading',
            message: 'Проверка промокода...'
        });

        try {
            const response = await apiService.applyPromo({
                user_id: telegramId,
                promo_code: promoCode,
                amount: 0
            });

            if (response.success) {
                if (response.promo_type === 'sub_days') {
                    // Сначала проверяем, есть ли устройства
                    try {
                        const devicesResponse = await apiService.getUserDevices(telegramId);
                        const hasDevices = devicesResponse.devices && devicesResponse.devices.length > 0;

                        if (!hasDevices) {
                            // НЕТ УСТРОЙСТВ - создаем новое устройство автоматически
                            setPromoState({
                                code: promoCode,
                                status: 'loading',
                                message: 'Создание устройства...'
                            });

                            // Создаем новое устройство через API
                            const createResponse = await apiService.createDeviceForPromo({
                                user_id: telegramId,
                                days: response.discount || 0,
                                promo_code: promoCode
                            });

                            if (createResponse.success) {
                                // Форматируем дату или используем значение по умолчанию
                                const expiryDate = createResponse.expires_at
                                    ? formatDateTime(createResponse.expires_at)
                                    : 'Дата не указана';

                                setSuccessDetails({
                                    deviceName: createResponse.device_name || 'Новое устройство',
                                    daysAdded: response.discount || 0,
                                    newExpiryDate: expiryDate,
                                    promoCode: promoCode,
                                    isNewDevice: true
                                });
                                setPromoCode('');
                                setPromoState({
                                    code: '',
                                    status: 'idle',
                                    message: ''
                                });
                            } else {
                                setPromoState({
                                    code: promoCode,
                                    status: 'error',
                                    message: createResponse.message || 'Ошибка создания устройства'
                                });
                            }
                            return;
                        }
                    } catch (error) {
                        // Если не удалось проверить, показываем выбор как обычно
                    }

                    // ЕСТЬ УСТРОЙСТВА - показываем выбор
                    setPromoState({
                        code: promoCode,
                        status: 'success',
                        message: response.message,
                        promoType: 'sub_days',
                        discount: response.discount
                    });
                    setAppliedPromoData(response);
                    setShowDeviceSelection(true);
                    onModalStateChange?.(true);
                } else if (response.promo_type === 'cash') {
                    setPromoState({
                        code: promoCode,
                        status: 'success',
                        message: response.message,
                        discount: response.discount,
                        promoType: 'cash'
                    });
                    setPromoCode('');
                } else {
                    setPromoState({
                        code: promoCode,
                        status: 'success',
                        message: response.message,
                        promoType: response.promo_type
                    });
                }
            } else {
                setPromoState({
                    code: promoCode,
                    status: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            setPromoState({
                code: promoCode,
                status: 'error',
                message: 'Ошибка при проверке промокода'
            });
        }
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return 'Дата не указана';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Дата не указана';
        }

        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleApplyToDevice = async () => {
        if (!telegramId || !appliedPromoData || !selectedDeviceId) return;

        setApplying(true);

        // Находим выбранное устройство
        const selectedDevice = devices?.devices.find(d => d.id === selectedDeviceId);
        const deviceName = selectedDevice ? formatDeviceName(selectedDevice) : 'устройство';

        try {
            const response = await apiService.applyPromoToDevice({
                user_id: telegramId,
                promo_code: promoState.code,
                device_id: selectedDeviceId,
                days: appliedPromoData.discount || 0
            });

            if (response.success) {
                // Сохраняем детали успешного продления
                let newExpiryDateFormatted = 'Дата не указана';

                if (response.new_expires_at) {
                    newExpiryDateFormatted = formatDateTime(response.new_expires_at);
                } else if (selectedDevice) {
                    // Обновляем устройство из списка
                    await loadDevices();
                    const updatedDevice = devices?.devices.find(d => d.id === selectedDeviceId);
                    if (updatedDevice) {
                        newExpiryDateFormatted = formatDateTime(updatedDevice.expires_at);
                    }
                }

                setSuccessDetails({
                    deviceName: deviceName,
                    daysAdded: appliedPromoData.discount || 0,
                    newExpiryDate: newExpiryDateFormatted,
                    promoCode: promoState.code,
                    isNewDevice: false
                });

                setShowDeviceSelection(false);
                setAppliedPromoData(null);
                setSelectedDeviceId(null);
                onModalStateChange?.(false);
                setPromoCode('');

                // Очищаем состояние промокода
                setPromoState({
                    code: '',
                    status: 'idle',
                    message: ''
                });
            } else {
                setPromoState({
                    code: promoState.code,
                    status: 'error',
                    message: response.message || 'Ошибка применения промокода'
                });
                setShowDeviceSelection(false);
                onModalStateChange?.(false);
            }
        } catch (error) {
            setPromoState({
                code: promoState.code,
                status: 'error',
                message: 'Ошибка при применении промокода'
            });
            setShowDeviceSelection(false);
            onModalStateChange?.(false);
        } finally {
            setApplying(false);
        }
    };

    const handleCloseModal = () => {
        setShowDeviceSelection(false);
        setAppliedPromoData(null);
        setSelectedDeviceId(null);
        setPromoState({
            code: '',
            status: 'idle',
            message: ''
        });
        onModalStateChange?.(false);
    };

    const handleCloseSuccess = () => {
        setSuccessDetails(null);
    };

    const getDeviceStatus = (device: any): { text: string; color: string } => {
        if (!device.is_configured) {
            return { text: 'Требуется настройка', color: 'yellow' };
        }

        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft < 0) {
            return { text: 'Просрочено', color: 'red' };
        }
        if (daysLeft === 0) {
            return { text: 'Заканчивается сегодня', color: 'yellow' };
        }
        return { text: `Осталось ${daysLeft} дн.`, color: 'green' };
    };

    const getDeviceIconType = (device: any): string => {
        if (!device.is_configured) return 'locked';

        const protocol = device.protocol_name?.toLowerCase() || '';
        if (protocol.includes('wireguard') || protocol.includes('wg')) return 'wireguard';
        if (protocol.includes('vless')) return 'rocket';
        if (protocol.includes('amnezia') || protocol.includes('awg')) return 'shield';
        return 'default';
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDeviceName = (device: any): string => {
        if (device.name) return device.name;
        if (device.protocol_name) return `${device.protocol_name} устройство`;
        return 'Устройство';
    };

    if (userLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="promo-page loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    if (!telegramId) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="promo-page">
                    <div className="promo-header">
                        <button className="back-button" onClick={() => navigate(-1)}>
                            <BackIcon />
                        </button>
                        <h1 className="promo-title">Промокод</h1>
                    </div>
                    <div className="error-container">
                        <div className="error-icon"><LockIcon /></div>
                        <h2>Необходима авторизация</h2>
                        <p>Для активации промокода нужно войти в систему</p>
                        <button className="continue-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="promo-page">
                <div className="promo-header">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <BackIcon />
                    </button>
                    <h1 className="promo-title">Промокод</h1>
                </div>

                {/* Детальное сообщение об успешном продлении/создании */}
                {successDetails && (
                    <div className="success-message detailed">
                        <div className="success-icon-large">
                            <CheckIcon />
                        </div>
                        <div className="success-content">
                            <div className="success-title">Промокод успешно активирован!</div>
                            <div className="success-details">
                                <div className="detail-row">
                                    <span className="detail-label">Промокод:</span>
                                    <span className="detail-value highlight">{successDetails.promoCode}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Устройство:</span>
                                    <span className="detail-value">{successDetails.deviceName}</span>
                                </div>
                                {successDetails.isNewDevice && (
                                    <div className="detail-row">
                                        <span className="detail-label">Статус:</span>
                                        <span className="detail-value highlight">Создано новое устройство</span>
                                    </div>
                                )}
                                <div className="detail-row">
                                    <span className="detail-label">Добавлено дней:</span>
                                    <span className="detail-value highlight">+{successDetails.daysAdded}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Новая дата:</span>
                                    <span className="detail-value">{successDetails.newExpiryDate}</span>
                                </div>
                            </div>
                            {successDetails.isNewDevice && (
                                <div className="new-device-note">
                                    Теперь выберите протокол для подключения в разделе "Мои устройства"
                                </div>
                            )}
                        </div>
                        <button
                            className="success-close-button"
                            onClick={handleCloseSuccess}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                )}

                {/* Основной контент */}
                <div className="promo-card">
                    <div className="promo-card-icon"><GiftIcon /></div>
                    <div className="promo-card-title">Введите промокод</div>
                    <div className="promo-card-subtitle">
                        Введите промокод, чтобы получить бонусы
                    </div>

                    <div className="promo-input-group2">
                        <input
                            type="text"
                            className="promo-input"
                            placeholder="Например: WELCOME2024"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            disabled={promoState.status === 'loading' || !!successDetails}
                        />
                        <button
                            className="promo-submit-btn"
                            onClick={handleApplyPromo}
                            disabled={!promoCode.trim() || promoState.status === 'loading' || !!successDetails}
                        >
                            {promoState.status === 'loading' ? <LoaderIcon /> : 'Активировать'}
                        </button>
                    </div>

                    {promoState.message && promoState.status !== 'success' && (
                        <div className={`promo-message ${promoState.status}`}>
                            {promoState.status === 'error' && <CloseIcon />}
                            {promoState.status === 'loading' && <LoaderIcon />}
                            {promoState.message}
                        </div>
                    )}

                    {promoState.status === 'success' && promoState.promoType === 'cash' && (
                        <div className="promo-success-card">
                            <div className="success-icon"><MoneyIcon /></div>
                            <p className="success-text">
                                Баланс пополнен на {promoState.discount}₽
                            </p>
                            <button
                                className="continue-button"
                                onClick={() => navigate('/profile')}
                            >
                                Перейти в профиль
                            </button>
                        </div>
                    )}
                </div>

                <div className="promo-info-card">
                    <div className="info-title">Как это работает?</div>
                    <ul className="info-list">
                        <li>
                            <CalendarIcon />
                            Промокоды на дни продлевают подписку
                        </li>
                        <li>
                            <PercentIcon />
                            Процентные скидки действуют при оплате
                        </li>
                    </ul>
                </div>
            </div>

            {/* Модальное окно выбора устройства */}
            {showDeviceSelection && devices && devices.devices.length > 0 && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="device-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Выберите устройство</h3>
                            <button className="close-button" onClick={handleCloseModal}>
                                <CloseModalIcon />
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="bonus-info">
                                <GiftIcon />
                                <span className="bonus-text">
                                    Вы активируете промокод <strong>{promoState.code}</strong><br />
                                    и продлите устройство на <strong>{appliedPromoData?.discount || 0} дней</strong>
                                </span>
                            </div>

                            <div className="devices-list">
                                {devices.devices.map(device => {
                                    const isSelected = selectedDeviceId === device.id;
                                    const status = getDeviceStatus(device);

                                    return (
                                        <div
                                            key={device.id}
                                            className={`device-option ${isSelected ? 'selected' : ''} ${status.color}`}
                                            onClick={() => setSelectedDeviceId(device.id)}
                                        >
                                            <div className="device-select">
                                                {isSelected && <CheckIcon />}
                                            </div>
                                            <div className="device-icon">
                                                <DeviceIcon type={getDeviceIconType(device)} />
                                            </div>
                                            <div className="device-info">
                                                <div className="device-name">{formatDeviceName(device)}</div>
                                                <div className="device-details">
                                                    {device.protocol_name && (
                                                        <span className="device-protocol">{device.protocol_name}</span>
                                                    )}
                                                    <span className="device-expires">
                                                        до {formatDate(device.expires_at)}
                                                    </span>
                                                </div>
                                                <div className={`device-status ${status.color}`}>
                                                    {status.text}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                className="confirm-button"
                                onClick={handleApplyToDevice}
                                disabled={!selectedDeviceId || applying}
                            >
                                {applying ? (
                                    <>
                                        <LoaderIcon />
                                        <span>Применение...</span>
                                    </>
                                ) : (
                                    `Продлить на ${appliedPromoData?.discount || 0} дней`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PromoPage;